import React, { useState, useEffect, useRef } from 'react';
import { 
  Building2,
  Save,
  Upload,
  Phone,
  Mail,
  Globe,
  MapPin,
  CreditCard,
  FileText,
  Image,
  X,
  Loader2,
  Lock,
  Database,
  HardDriveDownload,
  FileArchive
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { agencySettingsAPI } from '../lib/api';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { PaymentApisTab } from '../components/PaymentApisTab';
import { KeyRound } from 'lucide-react';
import { SignatureField } from '../components/SignatureField';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AgencySettings = () => {
  const [settings, setSettings] = useState({
    name: "GAB'S SERVICES HABITAT",
    slogan: 'Gestion Immobilière',
    logo_url: '',
    address: '',
    city: 'Bamako',
    country: 'Mali',
    phone: '',
    email: '',
    website: '',
    tax_id: '',
    bank_name: '',
    bank_account: '',
    bank_holder: '',
    orange_money_number: '',
    moov_money_number: '',
    legal_representative: '',
    official_stamp_base64: '',
    official_signature_base64: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [canEdit, setCanEdit] = useState(true);
  const fileInputRef = useRef(null);
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_admin', 'agent'].includes(user?.role);

  useEffect(() => {
    fetchSettings();
    fetchPermission();
  }, []);

  const fetchPermission = async () => {
    if (isAdmin) {
      setCanEdit(true);
      return;
    }
    try {
      const r = await api.get('/agent-permissions');
      setCanEdit(!!r.data?.permissions?.agency_settings);
    } catch (e) {
      setCanEdit(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await agencySettingsAPI.get();
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) {
      toast.error("Modification réservée aux administrateurs. Demandez l'accès à votre administrateur.");
      return;
    }
    setSaving(true);
    
    try {
      await agencySettingsAPI.update(settings);
      toast.success('Paramètres enregistrés avec succès');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'enregistrement';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!canEdit) {
      toast.error("Modification réservée aux administrateurs. Demandez l'accès à votre administrateur.");
      event.target.value = '';
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Type de fichier non autorisé. Utilisez: JPEG, PNG, GIF, WebP ou PDF');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (maximum 5MB)');
      return;
    }

    setUploading(true);
    try {
      const response = await agencySettingsAPI.uploadLogo(file);
      const logoUrl = response.data.logo_url;
      setSettings(prev => ({ ...prev, logo_url: logoUrl }));
      toast.success('Logo uploadé avec succès');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'upload';
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = () => {
    setSettings(prev => ({ ...prev, logo_url: '' }));
  };

  const getLogoFullUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="agency-settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
          Paramètres de l'Agence
        </h1>
        <p className="text-gray-500 mt-1">
          Configurez les informations qui apparaîtront sur les quittances
        </p>
      </div>

      {!canEdit && (
        <div
          className="flex items-start gap-3 bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-4"
          data-testid="agency-settings-readonly-banner"
        >
          <Lock className="w-5 h-5 mt-0.5 flex-shrink-0 text-amber-700" />
          <div className="text-sm">
            <p className="font-semibold mb-0.5">Mode lecture seule</p>
            <p>
              La modification des paramètres de l'agence est réservée aux administrateurs.
              Demandez à votre administrateur d'activer la permission « Paramètres de l'agence » pour pouvoir éditer ces informations.
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <fieldset disabled={!canEdit} className={!canEdit ? 'opacity-90' : ''}>
        <Tabs defaultValue="general" className="w-full" data-testid="agency-settings-tabs">
          <TabsList className="bg-[#FBF6EE] border border-[#E8D9C0] p-1 rounded-xl mb-6">
            <TabsTrigger
              value="general"
              className="rounded-lg data-[state=active]:bg-[#2B3A2C] data-[state=active]:text-white"
              data-testid="tab-general"
            >
              <Building2 className="w-4 h-4 mr-2" />
              Général
            </TabsTrigger>
            <TabsTrigger
              value="payment-apis"
              className="rounded-lg data-[state=active]:bg-[#B85C38] data-[state=active]:text-white"
              data-testid="tab-payment-apis"
            >
              <KeyRound className="w-4 h-4 mr-2" />
              API Paiement
            </TabsTrigger>
          </TabsList>

          <TabsContent value="general">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Identity Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#1B3A4B]" />
                Identité de l'Agence
              </CardTitle>
              <CardDescription>
                Informations principales de votre agence
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name">Nom de l'agence *</Label>
                <Input
                  id="name"
                  value={settings.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="ImmoMali"
                  data-testid="agency-name-input"
                />
              </div>
              
              <div>
                <Label htmlFor="slogan">Slogan</Label>
                <Input
                  id="slogan"
                  value={settings.slogan || ''}
                  onChange={(e) => handleChange('slogan', e.target.value)}
                  placeholder="Gestion Immobilière"
                  data-testid="agency-slogan-input"
                />
              </div>
              
              <div>
                <Label htmlFor="logo">Logo de l'agence</Label>
                <div className="mt-2">
                  {settings.logo_url ? (
                    <div className="flex items-start gap-4">
                      <div className="relative">
                        <div className="w-24 h-24 border border-gray-200 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
                          {settings.logo_url.endsWith('.pdf') ? (
                            <div className="text-center">
                              <FileText className="w-8 h-8 text-red-500 mx-auto" />
                              <span className="text-xs text-gray-500">PDF</span>
                            </div>
                          ) : (
                            <img 
                              src={getLogoFullUrl(settings.logo_url)} 
                              alt="Logo" 
                              className="w-full h-full object-contain"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                // Create error element safely without innerHTML (XSS prevention)
                                const errorSpan = document.createElement('span');
                                errorSpan.className = 'text-xs text-gray-400';
                                errorSpan.textContent = 'Erreur';
                                e.target.parentElement.appendChild(errorSpan);
                              }}
                            />
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={handleRemoveLogo}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                          title="Supprimer le logo"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-500 mb-2">Logo actuel</p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploading}
                        >
                          {uploading ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Upload...</>
                          ) : (
                            <><Upload className="w-4 h-4 mr-2" /> Changer</>
                          )}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#1B3A4B] transition-colors cursor-pointer"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {uploading ? (
                        <div className="flex flex-col items-center">
                          <Loader2 className="w-10 h-10 text-[#1B3A4B] animate-spin mb-2" />
                          <p className="text-sm text-gray-500">Upload en cours...</p>
                        </div>
                      ) : (
                        <>
                          <Image className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm font-medium text-gray-700">
                            Cliquez pour télécharger votre logo
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            PNG, JPG, GIF, WebP ou PDF (max 5MB)
                          </p>
                        </>
                      )}
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="logo-file-input"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="tax_id">Numéro Fiscal (NIF)</Label>
                <Input
                  id="tax_id"
                  value={settings.tax_id || ''}
                  onChange={(e) => handleChange('tax_id', e.target.value)}
                  placeholder="ML-XXXXX-XXXX"
                  data-testid="agency-taxid-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Contact Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Phone className="w-5 h-5 text-[#1B3A4B]" />
                Coordonnées
              </CardTitle>
              <CardDescription>
                Informations de contact
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="address">Adresse</Label>
                <Textarea
                  id="address"
                  value={settings.address || ''}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="Rue 305, Porte 112, ACI 2000"
                  rows={2}
                  data-testid="agency-address-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">Ville</Label>
                  <Input
                    id="city"
                    value={settings.city || ''}
                    onChange={(e) => handleChange('city', e.target.value)}
                    placeholder="Bamako"
                  />
                </div>
                <div>
                  <Label htmlFor="country">Pays</Label>
                  <Input
                    id="country"
                    value={settings.country || ''}
                    onChange={(e) => handleChange('country', e.target.value)}
                    placeholder="Mali"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="phone">Téléphone</Label>
                <Input
                  id="phone"
                  value={settings.phone || ''}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="+223 20 XX XX XX"
                  data-testid="agency-phone-input"
                />
              </div>
              
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={settings.email || ''}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="contact@immomali.ml"
                  data-testid="agency-email-input"
                />
              </div>
              
              <div>
                <Label htmlFor="website">Site Web</Label>
                <Input
                  id="website"
                  value={settings.website || ''}
                  onChange={(e) => handleChange('website', e.target.value)}
                  placeholder="www.immomali.ml"
                />
              </div>
            </CardContent>
          </Card>

          {/* Bank Section */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-[#1B3A4B]" />
                Coordonnées de paiement (visibles par les locataires)
              </CardTitle>
              <CardDescription>
                Ces informations apparaîtront sur les quittances et dans l&apos;espace locataire (options Mobile Money & virement).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="orange_money_number" className="flex items-center gap-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-orange-500"></span>
                    Numéro Orange Money
                  </Label>
                  <Input
                    id="orange_money_number"
                    value={settings.orange_money_number || ''}
                    onChange={(e) => handleChange('orange_money_number', e.target.value)}
                    placeholder="+223 90 12 37 14"
                    data-testid="agency-orange-money-input"
                  />
                </div>
                <div>
                  <Label htmlFor="moov_money_number" className="flex items-center gap-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-blue-500"></span>
                    Numéro Moov Money
                  </Label>
                  <Input
                    id="moov_money_number"
                    value={settings.moov_money_number || ''}
                    onChange={(e) => handleChange('moov_money_number', e.target.value)}
                    placeholder="+223 98 84 26 79"
                    data-testid="agency-moov-money-input"
                  />
                </div>
                <div>
                  <Label htmlFor="bank_name">Nom de la Banque</Label>
                  <Input
                    id="bank_name"
                    value={settings.bank_name || ''}
                    onChange={(e) => handleChange('bank_name', e.target.value)}
                    placeholder="BDM-SA"
                    data-testid="agency-bank-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="bank_account">Numéro de Compte / RIB</Label>
                  <Input
                    id="bank_account"
                    value={settings.bank_account || ''}
                    onChange={(e) => handleChange('bank_account', e.target.value)}
                    placeholder="ML XXXX XXXX XXXX XXXX"
                    data-testid="agency-bank-account-input"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="bank_holder">Titulaire du compte</Label>
                  <Input
                    id="bank_holder"
                    value={settings.bank_holder || ''}
                    onChange={(e) => handleChange('bank_holder', e.target.value)}
                    placeholder="GAB'S SERVICES SARL"
                    data-testid="agency-bank-holder-input"
                  />
                </div>
                <div>
                  <Label htmlFor="wave_number" className="flex items-center gap-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-cyan-500"></span>
                    Numéro Wave
                  </Label>
                  <Input
                    id="wave_number"
                    value={settings.wave_number || ''}
                    onChange={(e) => handleChange('wave_number', e.target.value)}
                    placeholder="+223 76 00 00 00"
                    data-testid="agency-wave-input"
                  />
                </div>
                <div>
                  <Label htmlFor="sama_money_number" className="flex items-center gap-1">
                    <span className="inline-block w-2 h-2 rounded-full bg-purple-500"></span>
                    Numéro Sama Money
                  </Label>
                  <Input
                    id="sama_money_number"
                    value={settings.sama_money_number || ''}
                    onChange={(e) => handleChange('sama_money_number', e.target.value)}
                    placeholder="+223 77 00 00 00"
                    data-testid="agency-sama-input"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="payment_instructions">Instructions au locataire (facultatif)</Label>
                  <textarea
                    id="payment_instructions"
                    rows={3}
                    value={settings.payment_instructions || ''}
                    onChange={(e) => handleChange('payment_instructions', e.target.value)}
                    placeholder="Ex : Merci d'indiquer votre code locataire et le mois concerné dans le motif du paiement."
                    className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    data-testid="agency-payment-instructions"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">Reprises intégralement sur la fiche PDF envoyée aux locataires.</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Représentant légal & Cachet officiel */}
          <Card className="lg:col-span-2 border-l-4 border-l-[#B85C38]">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Lock className="w-5 h-5 text-[#B85C38]" />
                Représentant légal & Cachet officiel
              </CardTitle>
              <CardDescription>
                Utilisés pour les documents RH (attestations, contrats, bulletins). Le cachet apparaît à côté de la signature dans les PDF.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="legal_representative">Nom du représentant légal</Label>
                  <Input
                    id="legal_representative"
                    value={settings.legal_representative || ''}
                    onChange={(e) => handleChange('legal_representative', e.target.value)}
                    disabled={!canEdit}
                    placeholder="M. Amadou DIALLO"
                    data-testid="agency-legal-rep-input"
                  />
                  <p className="text-[11px] text-gray-500 mt-2">
                    Pré-rempli automatiquement comme signataire par défaut sur les attestations.
                  </p>
                </div>
                <div>
                  <Label>Cachet officiel (PNG/JPG)</Label>
                  <div className="mt-1 flex items-center gap-3">
                    <label
                      htmlFor="agency-stamp-file"
                      className={`cursor-pointer inline-flex items-center gap-2 border border-dashed border-[#B85C38]/50 rounded-lg px-3 py-2 text-xs text-[#B85C38] hover:bg-[#B85C38]/5 ${!canEdit ? 'opacity-50 pointer-events-none' : ''}`}
                    >
                      <Upload className="w-3.5 h-3.5" />
                      {settings.official_stamp_base64 ? 'Changer le cachet' : 'Téléverser un cachet'}
                    </label>
                    <input
                      id="agency-stamp-file"
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      disabled={!canEdit}
                      data-testid="agency-stamp-file"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (!file.type.startsWith('image/')) return toast.error('Fichier image requis');
                        if (file.size > 2 * 1024 * 1024) return toast.error('Image trop volumineuse (max 2 Mo)');
                        const reader = new FileReader();
                        reader.onload = () => {
                          handleChange('official_stamp_base64', reader.result);
                          toast.success('Cachet chargé — pensez à enregistrer');
                        };
                        reader.readAsDataURL(file);
                      }}
                    />
                    {settings.official_stamp_base64 && canEdit && (
                      <button
                        type="button"
                        onClick={() => handleChange('official_stamp_base64', '')}
                        className="text-[11px] text-red-600 hover:underline"
                        data-testid="agency-stamp-clear"
                      >
                        Retirer
                      </button>
                    )}
                  </div>
                  {settings.official_stamp_base64 && (
                    <div className="mt-3 rounded-lg border border-[#B85C38]/30 bg-[#F5EFE1]/40 p-3 flex justify-center">
                      <img
                        src={settings.official_stamp_base64}
                        alt="Cachet officiel"
                        className="max-h-24 object-contain"
                        data-testid="agency-stamp-preview"
                      />
                    </div>
                  )}
                </div>
                <div className="md:col-span-2" data-testid="agency-signature-section">
                  <Label>Signature officielle de l'agence</Label>
                  <p className="text-[11px] text-gray-500 mt-1 mb-2">
                    Apposée en un clic (avec le cachet) sur les baux et états des lieux, par tout membre de l'équipe.
                  </p>
                  {canEdit ? (
                    <SignatureField value={settings.official_signature_base64 || ''} onChange={(v) => handleChange('official_signature_base64', v)} testIdPrefix="agency-signature" />
                  ) : settings.official_signature_base64 ? (
                    <img src={settings.official_signature_base64} alt="Signature" className="max-h-20 object-contain" />
                  ) : <p className="text-xs text-gray-400">Non configurée</p>}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Preview Section */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#1B3A4B]" />
                Aperçu sur Quittance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border border-gray-200 rounded-lg p-6 bg-white">
                <div className="flex items-start justify-between border-b pb-4">
                  <div className="flex items-center gap-4">
                    {settings.logo_url ? (
                      settings.logo_url.endsWith('.pdf') ? (
                        <div className="w-14 h-14 rounded-lg bg-red-50 flex items-center justify-center">
                          <FileText className="w-7 h-7 text-red-500" />
                        </div>
                      ) : (
                        <img 
                          src={getLogoFullUrl(settings.logo_url)} 
                          alt="Logo" 
                          className="h-14 object-contain"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      )
                    ) : (
                      <div className="w-14 h-14 rounded-lg bg-[#1B3A4B] flex items-center justify-center">
                        <Building2 className="w-7 h-7 text-white" />
                      </div>
                    )}
                    <div>
                      <h3 className="text-xl font-bold text-[#1B3A4B]">{settings.name || "GAB'S SERVICES HABITAT"}</h3>
                      <p className="text-sm text-gray-500">{settings.slogan || 'Gestion Immobilière'}</p>
                    </div>
                  </div>
                  <div className="text-right text-sm text-gray-600">
                    {settings.address && <p>{settings.address}</p>}
                    <p>{settings.city}, {settings.country}</p>
                    {settings.phone && <p>Tél: {settings.phone}</p>}
                    {settings.email && <p>{settings.email}</p>}
                    {settings.tax_id && <p className="text-xs mt-1">NIF: {settings.tax_id}</p>}
                  </div>
                </div>
                <div className="pt-4 text-center text-gray-400">
                  <p className="text-sm">[ Contenu de la quittance ]</p>
                </div>
                {(settings.bank_name || settings.bank_account) && (
                  <div className="mt-4 pt-4 border-t text-sm text-gray-600">
                    <p className="font-medium">Informations bancaires:</p>
                    {settings.bank_name && <p>Banque: {settings.bank_name}</p>}
                    {settings.bank_account && <p>Compte: {settings.bank_account}</p>}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Save Button */}
        {canEdit && (
          <div className="flex justify-end mt-6">
            <Button 
              type="submit" 
              className="bg-[#1B3A4B] hover:bg-[#142D3A]"
              disabled={saving}
              data-testid="save-agency-settings-btn"
            >
              {saving ? (
                <>Enregistrement...</>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer les paramètres
                </>
              )}
            </Button>
          </div>
        )}
          </TabsContent>

          <TabsContent value="payment-apis">
            <PaymentApisTab />
          </TabsContent>
        </Tabs>
        </fieldset>
      </form>

      {/* Sauvegarde / Export complet */}
      {(['admin', 'super_admin', 'agent'].includes(user?.role)) && (
        <BackupSection />
      )}
    </div>
  );
};

export default AgencySettings;


// ============== BackupSection (Sauvegarde / Export complet) ==============
const BackupSection = () => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get('/admin/backup/info');
        setInfo(r.data);
      } catch (e) { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const r = await api.get('/admin/backup', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Sauvegarde telechargee');
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde');
    }
    setDownloading(false);
  };

  return (
    <Card className="mt-6 border-[#D4A745]/40" data-testid="backup-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-[#1B3A4B]">
          <FileArchive className="h-5 w-5 text-[#D4A745]" />
          Sauvegarde complete
        </CardTitle>
        <CardDescription>
          Telechargez un fichier ZIP contenant toutes les donnees de votre agence (clients, biens, paiements, photos, factures...).
          Conservez-le dans un endroit sur comme assurance contre toute perte de donnees.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && <p className="text-sm text-gray-500">Chargement...</p>}
        {info && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <div className="bg-gray-50 rounded p-3">
              <div className="flex items-center gap-1 text-xs uppercase text-gray-600">
                <Database className="h-3 w-3" /> Enregistrements
              </div>
              <div className="text-2xl font-bold text-[#1B3A4B]">
                {info.total_records?.toLocaleString('fr-FR')}
              </div>
            </div>
            <div className="bg-gray-50 rounded p-3">
              <div className="flex items-center gap-1 text-xs uppercase text-gray-600">
                <Image className="h-3 w-3" /> Fichiers/Photos
              </div>
              <div className="text-2xl font-bold text-[#1B3A4B]">{info.files_count}</div>
            </div>
            <div className="bg-gray-50 rounded p-3">
              <div className="text-xs uppercase text-gray-600">Collections</div>
              <div className="text-2xl font-bold text-[#1B3A4B]">
                {Object.keys(info.collections || {}).length}
              </div>
            </div>
          </div>
        )}
        <Button
          onClick={handleDownload}
          disabled={downloading}
          data-testid="download-backup-btn"
          className="bg-[#D4A745] hover:bg-[#b88a30] text-white"
        >
          {downloading ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generation en cours...</>
          ) : (
            <><HardDriveDownload className="h-4 w-4 mr-2" /> Telecharger la sauvegarde complete (.zip)</>
          )}
        </Button>
        <p className="text-xs text-gray-500">
          Le fichier ZIP contient: <strong>data.json</strong> (toutes les donnees), <strong>files/</strong> (toutes les images et factures), <strong>metadata.json</strong> (info export) et un <strong>README.txt</strong>.
        </p>
      </CardContent>
    </Card>
  );
};
